import { Turnstile } from '@marsidev/react-turnstile';
import cn from 'classnames';
import { sanitize } from 'isomorphic-dompurify';
import { useState } from 'react';
import { FormSubmissionData } from 'src/pages/api/submit-form';

import { useDictionary } from 'src/hooks/useDictionary';
import { useRouteContext } from 'src/hooks/useRouteContext';

import { setJumpLinkAttributes } from 'src/utils/jumpLinks';

import CircleCheck from 'assets/icons/circle-check.svg';
import CircleError from 'assets/icons/circle-error.svg';

import Cta from 'components/BaseHelpers/Cta';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import { ComponentProps } from '../../../types/component-props';
import { CoreFormComponentData } from '../../../types/components';
import CheckboxInput from './components/CheckboxInput';
import TextInput from './components/TextInput/TextInput';

import styles from './CoreFormComponent.module.scss';

export type CoreFormComponentProps = ComponentProps & CoreFormComponentData;

const CoreFormComponent = (props: CoreFormComponentProps): JSX.Element => {
  const {
    header,
    description,
    fields,
    submitLabel,
    clearAllLabel,
    successMessage,
    errorMessage,
    jumpLinkLabel,
    formId,
  } = props.fields;

  const { requiredFieldsLabel } = useDictionary();
  const { routeData } = useRouteContext();

  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (fields.length === 0) {
    return <></>;
  }

  return (
    <div className={cn(styles.main, 'container-10')} {...setJumpLinkAttributes(jumpLinkLabel)}>
      <Text tag="h2" field={header} className={cn('heading-h3')} />
      <RichText field={description} />
      <span className="body-med">{requiredFieldsLabel}</span>
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          setIsError(false);
          setIsSuccess(false);

          const formData = new FormData(e.currentTarget);

          let turnstileToken = '';
          const formValues: Record<string, string | string[]> = {};

          for (const [key, value] of formData.entries()) {
            if (typeof value !== 'string' || value.trim() === '') {
              continue;
            }
            const santizedValue = sanitize(value.trim());
            if (key === 'cf-turnstile-response') {
              turnstileToken = santizedValue;
              continue;
            }
            formValues[key] = santizedValue;
          }

          fields
            .filter((field) => field.type === 'checkbox')
            .forEach((checkboxField) => {
              const checkboxes = formData.getAll(checkboxField.label) as string[];
              if (checkboxes.length > 0) {
                formValues[checkboxField.label] = checkboxes.map((value) => sanitize(value));
              }
            });

          const formSubmissionData: FormSubmissionData = {
            formId,
            formValues: formValues as Record<string, string | string[]>,
            turnstileToken,
            formUrl: routeData?.route.url || '',
          };

          setIsSubmitting(true);
          fetch('/api/submit-form', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formSubmissionData),
          })
            .then((response) => {
              if (!response.ok) {
                setIsError(true);
              } else {
                setIsSuccess(true);
              }
            })
            .catch((error) => {
              setIsError(true);
              console.error('Error submitting form:', error);
            })
            .finally(() => {
              setIsSubmitting(false);
            });
        }}
      >
        {fields.map((field, index) => {
          if (field.type === 'text' || field.type === 'email' || field.type === 'textarea') {
            return (
              <TextInput
                key={index}
                {...field}
                className={cn({
                  [styles['text-area']]: field.type === 'textarea',
                  [styles['email-input']]: field.type === 'email',
                  [styles['text-input']]: field.type === 'text',
                })}
              />
            );
          } else if (field.type === 'checkbox') {
            return <CheckboxInput key={index} {...field} className={styles['checkbox-input']} />;
          } else {
            const type: never = field.type;
            return type;
          }
        })}

        <Turnstile
          siteKey={routeData?.envVars.turnstileSiteKey || ''}
          options={{ theme: 'light' }}
          className={styles.turnstile}
        />

        {successMessage && isSuccess && (
          <div className={styles.success}>
            <CircleCheck className={styles['success-icon']} />
            <RichText field={successMessage} />
          </div>
        )}

        {errorMessage && isError && (
          <div className={styles.error}>
            <CircleError className={styles['error-icon']} />
            <RichText field={errorMessage} />
          </div>
        )}

        {!isSuccess && (
          <div className={styles.buttons}>
            <Cta
              tag="button"
              type="submit"
              onClick={() => {}}
              className={styles.submitButton}
              theme="filled"
              showIcon={true}
              disabled={isSubmitting}
            >
              {submitLabel}
            </Cta>
            <Cta
              tag="button"
              type="reset"
              onClick={() => {
                setIsError(false);
                setIsSuccess(false);
              }}
              className={styles.clearButton}
              disabled={isSubmitting}
            >
              {clearAllLabel}
            </Cta>
          </div>
        )}
      </form>
    </div>
  );
};

export default CoreFormComponent;
